import { Check, Copy, Share2 } from "lucide-react";
import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FacebookIcon,
  GmailIcon,
  MailIcon,
  TwitterIcon,
  WhatsAppIcon,
} from "../SocialMediaIcons";
import { useLocation } from "react-router-dom";

interface ShareButtonProps {
  url?: string;
  title?: string;
  className?: string;
}

interface SocialPlatform {
  name: string;
  icon: React.ReactNode;
  createShareUrl: (url: string, title: string) => string;
}

const platforms: SocialPlatform[] = [
  {
    name: "WhatsApp",
    icon: <WhatsAppIcon />,
    createShareUrl: (url, title) =>
      `https://api.whatsapp.com/send?text=${encodeURIComponent(
        title
      )}%20${encodeURIComponent(url)}`,
  },
  {
    name: "Gmail",
    icon: <GmailIcon />,
    createShareUrl: (url, title) => {
      const gmailUrl = new URL("https://mail.google.com/mail/");
      gmailUrl.searchParams.append("view", "cm");
      gmailUrl.searchParams.append("fs", "1");
      gmailUrl.searchParams.append("su", title); // 'su' is for Subject
      gmailUrl.searchParams.append("body", url); // 'body' is for Body
      return gmailUrl.href;
    },
  },
  {
    name: "Email",
    icon: <MailIcon />,
    createShareUrl: (url, title) => {
      const subject = encodeURIComponent(title);
      const body = encodeURIComponent(
        `I thought you might find this page interesting:\n\n${url}`
      );
      return `mailto:?subject=${subject}&body=${body}`;
    },
  },
  {
    name: "Twitter",
    icon: <TwitterIcon />,
    createShareUrl: (url, title) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(title)}`,
  },
  {
    name: "Facebook",
    icon: <FacebookIcon />,
    createShareUrl: (url, _title) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
];

export function ShareButton({ url, title, className }: ShareButtonProps) {
  const location = useLocation();
  const [hasCopied, setHasCopied] = useState(false);
  const [displayUrl, setDisplayUrl] = useState("");

  // const shareUrl =
  //   url || (typeof window !== "undefined" ? window.location.href : "");
  // const shareTitle =
  //   title || (typeof document !== "undefined" ? document.title : "");

  useEffect(() => {
    // const currentUrl = url || window.location.href;
    const { pathname, search, hash } = location;
    const currentUrl = `${window.location.origin}${pathname}${search}${hash}`;
    setDisplayUrl(url || currentUrl);
  }, [location, url]);

  useEffect(() => {
    if (hasCopied) {
      const timer = setTimeout(() => {
        setHasCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasCopied]);

  const getLatestUrl = () => {
    if (url) return url;

    const { pathname, search, hash } = location;
    return `${window.location.origin}${pathname}${search}${hash}`;
  };

  const handleCopy = () => {
    const shareUrl = getLatestUrl();
    navigator.clipboard.writeText(shareUrl).then(() => {
      setHasCopied(true);
    });
  };

  const handleSocialShare = (
    createUrl: (url: string, title: string) => string
  ) => {
    const shareUrl = getLatestUrl();
    // const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;

    const finalUrl = createUrl(shareUrl, shareTitle);
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className={className}>
          <Share2 className="h-4 w-4" />
          <span className="sr-only">Share</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">Share this page</h4>
            <p className="text-sm text-muted-foreground">
              Anyone with the link can view this page.
            </p>
          </div>

          <div className="flex justify-around items-center gap-2">
            {platforms.map((platform) => (
              <Button
                key={platform.name}
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={() => handleSocialShare(platform.createShareUrl)}
              >
                {platform.icon}
                <span className="sr-only">{platform.name}</span>
              </Button>
            ))}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="link" className="sr-only">
              Link
            </Label>
            <div className="flex items-center space-x-2">
              <Input
                id="link"
                value={displayUrl}
                readOnly
                className="h-9 flex-1"
              />
              <Button
                type="submit"
                size="sm"
                className="px-3"
                onClick={handleCopy}
              >
                <span className="sr-only">Copy</span>
                {hasCopied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
